import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Clipboard, Pencil, Search, Smartphone, Trash2 } from 'lucide-react';
import { ImeiLookupPanel } from '@/components/devices/ImeiLookupPanel.js';
import { M360DiagnosticsPanel } from '@/components/ticket-detail/M360DiagnosticsPanel.js';
import { getTicketM360Diagnostics } from '@/lib/api/m360.js';
import { useIntakeSectionOpen } from '@/lib/ticket-intake-collapse.js';
import {
  DevicePriceChangeDialog,
  EditTicketDeviceModal,
  type DeviceEditPricePrompt,
} from '@/components/ticket-detail/EditTicketDeviceModal.js';
import type {
  ConditionStatus,
  DeviceConditionItem,
  PatchTicketDeviceBody,
  SuppliedItem,
  TicketDevice,
  TicketLineItem,
  TicketRecord,
  TicketRepair,
  TicketTimeEntry,
} from '@/lib/api/tickets.js';
import type { PartsRequest } from '@/lib/api/parts-requests.js';
import type { UserListItem } from '@/lib/api/users.js';
import type { TemplateItem } from '@/lib/api/device-condition-templates.js';
import type { TicketPart } from '@/lib/api/ticket-parts.js';
import { RepairRow } from '@/components/tickets/RepairRow.js';
import { PhotosGrid } from '@/components/tickets/PhotosGrid.js';
import { AddRepairForm } from '@/components/tickets/AddRepairForm.js';
import { formatDateTime, prettyLabel } from '@/lib/format.js';
import { createTimeEntry, patchTimeEntry } from '@/lib/api/tickets.js';
import { useRunningTimerClock } from '@/hooks/useRunningTimerClock.js';
import { clearRunningTimer, formatClockSeconds, writeRunningTimer } from '@/lib/running-timer-storage.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const CONDITION_LABELS: Record<ConditionStatus, string> = {
  working: 'Working',
  not_working: 'Not Working',
  not_tested: 'Not Tested',
  na: 'N/A',
};

export function deviceLabel(device: TicketDevice) {
  return (
    [device.makeName, device.modelName].filter(Boolean).join(' ') ||
    [device.makeName, device.customModelName].filter(Boolean).join(' ') ||
    device.customModelName ||
    'Device'
  );
}

function mergeTemplateConditions(
  templateItems: TemplateItem[],
  saved: DeviceConditionItem[] | undefined,
): DeviceConditionItem[] {
  const savedMap = new Map((saved ?? []).map((i) => [i.name, i]));
  return templateItems.map((t) => ({
    name: t.name,
    status: savedMap.get(t.name)?.status ?? 'not_tested',
  }));
}

function recordedCount(items: DeviceConditionItem[]): number {
  return items.filter((i) => i.status !== 'not_tested').length;
}

function m360SummaryChip(
  pre: { ticketDeviceId: string | null; diagnosticType?: string } | null | undefined,
  post: { ticketDeviceId: string | null; diagnosticType?: string } | null | undefined,
  history: Array<{ ticketDeviceId: string | null; diagnosticType: string }>,
  deviceId: string,
): string {
  const matchesDevice = (row: { ticketDeviceId: string | null }) =>
    !row.ticketDeviceId || row.ticketDeviceId === deviceId;
  const hasPre =
    Boolean(pre && matchesDevice(pre)) ||
    history.some((r) => matchesDevice(r) && r.diagnosticType === 'pre');
  const hasPost =
    Boolean(post && matchesDevice(post)) ||
    history.some((r) => matchesDevice(r) && r.diagnosticType === 'post');
  if (!hasPre && !hasPost) return 'no report';
  if (hasPre && hasPost) return 'pre + post';
  if (hasPre) return 'pre report';
  return 'post report';
}

function CollapsibleSection({
  title,
  summary,
  open,
  onToggle,
  children,
}: {
  title: string;
  summary?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-slate-100">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50"
        onClick={onToggle}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        <span>{title}</span>
        {summary ? (
          <span className="ml-auto shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500">
            {summary}
          </span>
        ) : null}
      </button>
      {open ? <div className="px-4 pb-4">{children}</div> : null}
    </div>
  );
}

function ConditionsGrid({
  items,
  onChange,
  onSelectAllWorking,
}: {
  items: DeviceConditionItem[];
  onChange: (items: DeviceConditionItem[]) => void;
  onSelectAllWorking: () => void;
}) {
  return (
    <div>
      <button type="button" className="mb-3 text-xs font-semibold text-primary hover:underline" onClick={onSelectAllWorking}>
        Select All: Working
      </button>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, idx) => (
          <label key={item.name} className="rounded-xl border border-slate-200 p-2 text-sm">
            <span className="mb-1 block font-medium text-slate-800">{item.name}</span>
            <select
              className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs"
              value={item.status}
              onChange={(e) => {
                const next = [...items];
                next[idx] = { ...item, status: e.target.value as ConditionStatus };
                onChange(next);
              }}
            >
              {(Object.keys(CONDITION_LABELS) as ConditionStatus[]).map((s) => (
                <option key={s} value={s}>{CONDITION_LABELS[s]}</option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </div>
  );
}

function PhotoCaptureModal({
  ticketNumber,
  deviceId,
  photoType,
  onClose,
}: {
  ticketNumber: string;
  deviceId: string;
  photoType: 'pre' | 'post';
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const base = window.location.origin;
  const params = new URLSearchParams({ ticket: ticketNumber, deviceId, type: photoType });
  const url = `${base}/photo?${params.toString()}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;

  function copyLink() {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-950">Open Photo Capture</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:text-slate-700">✕</button>
        </div>

        <p className="mb-4 text-sm text-slate-600">
          Scan this QR code on a phone to capture{' '}
          <span className="font-semibold">{photoType === 'pre' ? 'pre-repair' : 'post-repair'}</span> photos for{' '}
          <span className="font-semibold">{ticketNumber}</span>.
        </p>

        <div className="mb-4 flex justify-center">
          <img
            src={qrSrc}
            alt="QR code for photo capture"
            className="h-48 w-48 rounded-xl border border-slate-200 shadow-sm"
          />
        </div>

        <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="flex-1 truncate text-xs text-slate-600">{url}</span>
          <button
            type="button"
            onClick={copyLink}
            className="shrink-0 rounded-lg bg-primary px-2.5 py-1 text-xs font-semibold text-white"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full rounded-xl bg-slate-100 py-2 text-center text-sm font-semibold text-slate-700 hover:bg-slate-200"
        >
          Open in new tab ↗
        </a>
      </div>
    </div>
  );
}

function DeviceImeiHeaderRow({
  device,
  onPatchDevice,
  canEdit = true,
}: {
  device: TicketDevice;
  onPatchDevice: (deviceId: string, body: PatchTicketDeviceBody) => void;
  canEdit?: boolean;
}) {
  const [imei, setImei] = useState(device.imei ?? '');
  const [lookupOpen, setLookupOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const lastSavedRef = useRef((device.imei ?? '').replace(/\D/g, '').slice(0, 15));

  useEffect(() => {
    setImei(device.imei ?? '');
    lastSavedRef.current = (device.imei ?? '').replace(/\D/g, '').slice(0, 15);
  }, [device.id, device.imei]);

  useEffect(() => {
    if (!canEdit) return;
    const normalized = imei.replace(/\D/g, '').slice(0, 15);
    if (normalized === lastSavedRef.current) return;
    const t = setTimeout(() => {
      lastSavedRef.current = normalized;
      onPatchDevice(device.id, { imei: normalized || null });
    }, 600);
    return () => clearTimeout(t);
  }, [imei, device.id, onPatchDevice, canEdit]);

  function copyImei() {
    const val = imei.replace(/\D/g, '').slice(0, 15);
    if (!val) return;
    void navigator.clipboard.writeText(val).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">IMEI</label>
        <input
          className="min-w-[10rem] flex-1 rounded-lg border border-slate-300 px-2 py-1 font-mono text-sm disabled:bg-slate-50"
          value={imei}
          onChange={(e) => setImei(e.target.value.replace(/\D/g, '').slice(0, 15))}
          placeholder="Enter IMEI"
          inputMode="numeric"
          disabled={!canEdit}
        />
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          onClick={copyImei}
          title="Copy IMEI"
        >
          <Clipboard className="h-3.5 w-3.5" />
          {copied ? 'Copied' : 'Copy'}
        </button>
        {canEdit ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/5"
            onClick={() => setLookupOpen((o) => !o)}
          >
            <Search className="h-3.5 w-3.5" />
            Lookup
          </button>
        ) : null}
      </div>
      {lookupOpen && canEdit ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <ImeiLookupPanel value={imei} onChange={setImei} onApplyDevice={() => setLookupOpen(false)} />
        </div>
      ) : null}
    </div>
  );
}

export function DeviceCardDetail({
  device,
  ticket,
  repairs,
  lineItems,
  partsRequests,
  users,
  timeEntries,
  ticketId,
  ticketNumber,
  conditionTemplate,
  suppliedItems,
  showSuppliedItems,
  onRepairAssign,
  onRepairComplete,
  onRepairStatusChange,
  onRepairRemove,
  onOrderPart,
  onRepairLineDiscount,
  onRepairPriceUpdate,
  partsByRepair,
  trackSerialParts,
  onRemovePart,
  onRefetch,
  onPatchDevice,
  onRemoveDevice,
  otherDevices = [],
  onPatchSuppliedItems,
  onUploadPhoto,
  onDeletePhoto,
  currentUserId,
}: {
  device: TicketDevice;
  ticket: TicketRecord;
  repairs: TicketRepair[];
  lineItems: TicketLineItem[];
  partsRequests: PartsRequest[];
  users: UserListItem[];
  timeEntries: TicketTimeEntry[];
  ticketId: string;
  ticketNumber: string;
  conditionTemplate: TemplateItem[];
  suppliedItems: SuppliedItem[];
  showSuppliedItems: boolean;
  onRepairAssign: (repairId: string, userId: string | null) => void;
  onRepairComplete: (repairId: string) => void;
  onRepairStatusChange?: (repairId: string, status: 'pending' | 'in_progress' | 'completed') => void;
  onRepairRemove: (repairId: string) => void;
  onOrderPart: (repair: TicketRepair) => void;
  onRepairLineDiscount: (lineId: string, body: Record<string, unknown>) => void;
  onRepairPriceUpdate: (
    repairId: string,
    quotedPrice: number,
    meta?: { managerOverrideAuthorizedBy?: string },
  ) => void;
  partsByRepair: Map<string, TicketPart[]>;
  trackSerialParts?: boolean;
  onRemovePart: (partId: string) => void;
  onRefetch: () => void;
  onPatchDevice: (deviceId: string, body: PatchTicketDeviceBody) => void | Promise<void>;
  onRemoveDevice?: (deviceId: string, reassignToDeviceId?: string) => void | Promise<void>;
  otherDevices?: TicketDevice[];
  onPatchSuppliedItems: (items: SuppliedItem[]) => void;
  onUploadPhoto: (deviceId: string, photoType: 'pre' | 'post', dataUrl: string, caption: string) => void;
  onDeletePhoto: (deviceId: string, photoType: 'pre' | 'post', index: number) => void;
  currentUserId?: string;
}) {
  const [addMode, setAddMode] = useState<'catalog' | 'custom' | null>(null);
  const [photoTab, setPhotoTab] = useState<'pre' | 'post'>('pre');
  const [customSupplied, setCustomSupplied] = useState('');
  const [photoCaptureModal, setPhotoCaptureModal] = useState<'pre' | 'post' | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pricePrompt, setPricePrompt] = useState<DeviceEditPricePrompt | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const ticketClosed = ticket.status === 'closed' || ticket.status === 'voided' || ticket.status === 'cancelled';
  const canEditDevice = !ticketClosed;

  const preConditions = useMemo(
    () => mergeTemplateConditions(conditionTemplate, device.preConditions),
    [conditionTemplate, device.preConditions],
  );
  const postConditions = useMemo(
    () => mergeTemplateConditions(conditionTemplate, device.postConditions),
    [conditionTemplate, device.postConditions],
  );

  const [localPre, setLocalPre] = useState(preConditions);
  const [localPost, setLocalPost] = useState(postConditions);
  const [localSupplied, setLocalSupplied] = useState(suppliedItems);

  useEffect(() => setLocalPre(preConditions), [preConditions]);
  useEffect(() => setLocalPost(postConditions), [postConditions]);
  useEffect(() => setLocalSupplied(suppliedItems), [suppliedItems]);

  // Debounced autosave — only when local state differs from server props (skips mount)
  useEffect(() => {
    if (JSON.stringify(localPre) === JSON.stringify(preConditions)) return;
    const t = setTimeout(() => {
      onPatchDevice(device.id, { preConditions: localPre });
    }, 1000);
    return () => clearTimeout(t);
  }, [localPre, preConditions, device.id, onPatchDevice]);

  useEffect(() => {
    if (JSON.stringify(localPost) === JSON.stringify(postConditions)) return;
    const t = setTimeout(() => {
      onPatchDevice(device.id, { postConditions: localPost });
    }, 1000);
    return () => clearTimeout(t);
  }, [localPost, postConditions, device.id, onPatchDevice]);

  useEffect(() => {
    if (!showSuppliedItems) return;
    if (JSON.stringify(localSupplied) === JSON.stringify(suppliedItems)) return;
    const t = setTimeout(() => {
      onPatchSuppliedItems(localSupplied);
    }, 1000);
    return () => clearTimeout(t);
  }, [localSupplied, suppliedItems, showSuppliedItems, onPatchSuppliedItems]);

  const deviceTimeEntries = timeEntries.filter((e) =>
    repairs.some((r) => r.id === e.ticketRepairId),
  );
  const totalSeconds = deviceTimeEntries.reduce((s, e) => s + (e.durationSeconds ?? 0), 0);
  const anyRepairDone = repairs.some((r) => r.status === 'completed');
  const postRepairEnabled =
    anyRepairDone || ['in_repair', 'ready', 'closed', 'pending_pay'].includes(ticket.status);

  const linkedLineItems = new Map<string, TicketLineItem[]>();
  lineItems.forEach((line) => {
    if (!line.ticketRepairId) return;
    linkedLineItems.set(line.ticketRepairId, [...(linkedLineItems.get(line.ticketRepairId) ?? []), line]);
  });

  const primaryRepair = repairs[0];
  const { payload, elapsed } = useRunningTimerClock();
  const timerRunning = Boolean(payload?.ticketId === ticketId && repairs.some((r) => r.id === payload?.repairId));

  async function startTimer() {
    if (!primaryRepair || !currentUserId) return;
    try {
      const entry = await createTimeEntry(ticketId, {
        ticketRepairId: primaryRepair.id,
        entryType: 'timer',
        startedAt: new Date().toISOString(),
        billable: true,
      });
      writeRunningTimer({
        ticketId,
        ticketNumber,
        entryId: entry.id,
        repairId: primaryRepair.id,
        repairName: primaryRepair.serviceName,
        startedAt: entry.startedAt ?? new Date().toISOString(),
      });
      toastSuccess('Timer started');
      onRefetch();
    } catch (e) {
      toastError(parseApiError(e).message);
    }
  }

  async function stopTimer() {
    if (!payload) return;
    try {
      await patchTimeEntry(ticketId, payload.entryId, { endedAt: new Date().toISOString() });
      clearRunningTimer();
      toastSuccess('Timer stopped');
      onRefetch();
    } catch (e) {
      toastError(parseApiError(e).message);
    }
  }

  const preRecorded = recordedCount(localPre);
  const postRecorded = recordedCount(localPost);
  const preSummary = `${localPre.length} items · ${preRecorded} recorded`;
  const postSummary = postRecorded === 0 ? 'not recorded' : `${localPost.length} items · ${postRecorded} recorded`;
  const accessories = device.accessoriesReceived?.join(', ') || 'None';
  const checkedSupplied = localSupplied.filter((i) => i.checked);
  const suppliedSummary = `${checkedSupplied.length}/${localSupplied.length} checked`;
  const photoCount = (device.photosPre?.length ?? 0) + (device.photosPost?.length ?? 0);
  const photoSummary = `${photoCount} photo${photoCount === 1 ? '' : 's'}`;

  const m360Query = useQuery({
    queryKey: ['m360-ticket', ticketId],
    queryFn: () => getTicketM360Diagnostics(ticketId),
  });
  const m360Summary = m360SummaryChip(
    m360Query.data?.pre,
    m360Query.data?.post,
    m360Query.data?.history ?? [],
    device.id,
  );

  const preNeedsAttention = ticket.status === 'intake' && localPre.length > 0 && preRecorded === 0;
  const postNeedsAttention = anyRepairDone && localPost.length > 0 && postRecorded === 0;

  const suppliedSection = useIntakeSectionOpen('supplied', currentUserId, false);
  const preSection = useIntakeSectionOpen('preConditions', currentUserId, preNeedsAttention);
  const postSection = useIntakeSectionOpen('postConditions', currentUserId, postNeedsAttention);
  const photosSection = useIntakeSectionOpen('photos', currentUserId, false);
  const m360Section = useIntakeSectionOpen('m360', currentUserId, false);

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="border-b border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <Smartphone className="mt-0.5 h-5 w-5 text-primary" />
            <div>
              <h3 className="text-lg font-bold text-slate-950">{deviceLabel(device)}</h3>
              <p className="text-xs text-slate-600">
                {device.conditionAtIntake ? `${prettyLabel(device.conditionAtIntake)} condition` : 'Assess condition'} · {prettyLabel(device.passcodeType ?? 'unknown')} · Accessories: {accessories}
              </p>
              <DeviceImeiHeaderRow
                device={device}
                onPatchDevice={onPatchDevice}
                canEdit={canEditDevice}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEditDevice ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => setEditOpen(true)}
                title="Edit device"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
            ) : null}
            {canEditDevice && onRemoveDevice ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                onClick={() => {
                  setReassignTo(otherDevices[0]?.id ?? '');
                  setRemoveOpen(true);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </button>
            ) : null}
            {device.workStatus ? (
              <span
                className={[
                  'rounded-full px-2.5 py-1 text-xs font-semibold',
                  device.workStatus === 'repaired'
                    ? 'bg-emerald-100 text-emerald-800'
                    : device.workStatus === 'in_repair'
                      ? 'bg-blue-100 text-blue-800'
                      : device.workStatus === 'waiting_parts'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-700',
                ].join(' ')}
              >
                {prettyLabel(device.workStatus)}
              </span>
            ) : null}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
          {ticket.promisedDate ? <span>Due: {formatDateTime(ticket.promisedDate)}</span> : null}
          <span className="font-mono font-bold text-slate-800">
            ⏱ {formatClockSeconds(timerRunning ? elapsed : totalSeconds)}
          </span>
          {timerRunning ? (
            <button type="button" className="rounded-lg bg-red-100 px-2 py-1 text-xs font-bold text-red-800" onClick={() => void stopTimer()}>■ Stop</button>
          ) : (
            <button type="button" className="rounded-lg bg-green-100 px-2 py-1 text-xs font-bold text-green-800" onClick={() => void startTimer()}>▶ Start</button>
          )}
        </div>
      </header>

      <div className="p-4">
        <h4 className="mb-2 text-sm font-bold text-slate-800">Repairs</h4>
        <div className="space-y-2">
          {repairs.map((repair) => (
            <RepairRow
              key={repair.id}
              repair={repair}
              lineItems={linkedLineItems.get(repair.id) ?? []}
              parts={partsByRepair.get(repair.id) ?? []}
              partsRequests={partsRequests.filter((p) => p.ticketRepairId === repair.id)}
              users={users}
              ticketId={ticketId}
              trackSerialParts={trackSerialParts}
              deviceModelId={device.modelId}
              onAssign={onRepairAssign}
              onComplete={onRepairComplete}
              onStatusChange={onRepairStatusChange}
              onRemove={onRepairRemove}
              onOrderPart={onOrderPart}
              onApplyDiscount={onRepairLineDiscount}
              onUpdatePrice={onRepairPriceUpdate}
              onRemovePart={onRemovePart}
              onPartsChanged={onRefetch}
            />
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className="text-sm font-semibold text-primary" onClick={() => setAddMode('catalog')}>+ Add Repair from Catalog</button>
          <button type="button" className="text-sm font-semibold text-primary" onClick={() => setAddMode('custom')}>+ Custom Repair</button>
        </div>
        {addMode ? (
          <div className="mt-3 rounded-xl border border-slate-200 p-3">
            <AddRepairForm
              ticketId={ticketId}
              deviceId={device.id}
              makeId={device.makeId}
              modelId={device.modelId}
              initialMode={addMode}
              onSuccess={() => {
                setAddMode(null);
                onRefetch();
              }}
            />
          </div>
        ) : null}
      </div>

      {showSuppliedItems ? (
        <CollapsibleSection
          title="Supplied Items"
          summary={suppliedSummary}
          open={suppliedSection.open}
          onToggle={suppliedSection.toggle}
        >
          <div className="space-y-2">
            {localSupplied.map((item, idx) => (
              <label key={`${item.name}-${idx}`} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={(e) => {
                    const next = [...localSupplied];
                    next[idx] = { ...item, checked: e.target.checked };
                    setLocalSupplied(next);
                  }}
                />
                {item.name}
              </label>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-sm"
              placeholder="Custom item name"
              value={customSupplied}
              onChange={(e) => setCustomSupplied(e.target.value)}
            />
            <button
              type="button"
              className="rounded-lg bg-primary px-3 py-1 text-sm font-semibold text-white"
              onClick={() => {
                if (!customSupplied.trim()) return;
                setLocalSupplied([...localSupplied, { name: customSupplied.trim(), checked: false }]);
                setCustomSupplied('');
              }}
            >
              + Add
            </button>
          </div>
        </CollapsibleSection>
      ) : null}

      <CollapsibleSection
        title="Pre-Repair Conditions"
        summary={preSummary}
        open={preSection.open}
        onToggle={preSection.toggle}
      >
        <ConditionsGrid
          items={localPre}
          onChange={setLocalPre}
          onSelectAllWorking={() => setLocalPre(localPre.map((i) => ({ ...i, status: 'working' })))}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Post-Repair Conditions"
        summary={postSummary}
        open={postSection.open}
        onToggle={postSection.toggle}
      >
        {postRepairEnabled ? (
          <ConditionsGrid
            items={localPost}
            onChange={setLocalPost}
            onSelectAllWorking={() => setLocalPost(localPost.map((i) => ({ ...i, status: 'working' })))}
          />
        ) : (
          <p className="text-sm text-slate-500">Available after repair starts</p>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Pre/Post Photos"
        summary={photoSummary}
        open={photosSection.open}
        onToggle={photosSection.toggle}
      >
        <div className="mb-3 flex items-center justify-end">
          <button
            type="button"
            onClick={() => setPhotoCaptureModal(photoTab)}
            className="rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary/20"
          >
            📱 Open Photo Capture
          </button>
        </div>
        <div className="mb-3 flex gap-2">
          {(['pre', 'post'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={`rounded-lg px-3 py-1 text-xs font-semibold ${photoTab === tab ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'}`}
              onClick={() => setPhotoTab(tab)}
            >
              {tab === 'pre' ? 'Pre-Repair' : 'Post-Repair'}
            </button>
          ))}
        </div>
        <PhotosGrid
          photos={photoTab === 'pre' ? (device.photosPre ?? []) : (device.photosPost ?? [])}
          onAdd={(dataUrl, caption) => onUploadPhoto(device.id, photoTab, dataUrl, caption)}
          onDelete={(idx) => onDeletePhoto(device.id, photoTab, idx)}
        />
      </CollapsibleSection>

      {photoCaptureModal !== null ? (
        <PhotoCaptureModal
          ticketNumber={ticketNumber}
          deviceId={device.id}
          photoType={photoCaptureModal}
          onClose={() => setPhotoCaptureModal(null)}
        />
      ) : null}

      <CollapsibleSection
        title="M360 Diagnostics"
        summary={m360Summary}
        open={m360Section.open}
        onToggle={m360Section.toggle}
      >
        <M360DiagnosticsPanel
          ticketId={ticketId}
          ticketDeviceId={device.id}
          imei={device.imei}
          serial={device.serialNumber}
          embedded
          onApplyGrading={(condition) => {
            onPatchDevice(device.id, { conditionAtIntake: condition });
            toastSuccess(`Condition set to ${condition} from M360 grading`);
          }}
        />
      </CollapsibleSection>

      <EditTicketDeviceModal
        open={editOpen}
        device={device}
        repairs={repairs}
        canEdit={canEditDevice}
        onClose={() => setEditOpen(false)}
        onSave={(body) => {
          void Promise.resolve(onPatchDevice(device.id, body)).then(() => {
            setEditOpen(false);
            toastSuccess('Device updated');
          });
        }}
        onPriceConfirmNeeded={(prompt) => {
          setEditOpen(false);
          setPricePrompt(prompt);
        }}
      />

      <DevicePriceChangeDialog
        open={Boolean(pricePrompt)}
        lines={pricePrompt?.repairs ?? []}
        onCancel={() => setPricePrompt(null)}
        onKeep={() => {
          if (!pricePrompt) return;
          void Promise.resolve(
            onPatchDevice(device.id, { ...pricePrompt.body, updateRepairPrices: false }),
          ).then(() => {
            setPricePrompt(null);
            toastSuccess('Device updated (prices kept)');
          });
        }}
        onUpdate={() => {
          if (!pricePrompt) return;
          void Promise.resolve(
            onPatchDevice(device.id, { ...pricePrompt.body, updateRepairPrices: true }),
          ).then(() => {
            setPricePrompt(null);
            toastSuccess('Device updated (prices updated)');
          });
        }}
      />

      {removeOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-bold text-slate-950">Remove device?</h3>
            {otherDevices.length === 0 ? (
              <p className="mt-2 text-sm text-amber-800">
                This is the last device on the ticket. You can still remove it — the ticket will have no
                device until another is added.
              </p>
            ) : null}
            {repairs.length > 0 ? (
              <div className="mt-3 text-sm">
                <p className="font-medium text-slate-800">
                  {repairs.length} repair(s) are attached. Reassign them to another device:
                </p>
                {otherDevices.length === 0 ? (
                  <p className="mt-2 text-red-700">
                    No other device on this ticket — remove repairs first, or add another device.
                  </p>
                ) : (
                  <select
                    className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1.5"
                    value={reassignTo}
                    onChange={(e) => setReassignTo(e.target.value)}
                  >
                    {otherDevices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {[d.makeName, d.modelName || d.customModelName].filter(Boolean).join(' ') ||
                          'Device'}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <p className="mt-2 text-sm text-slate-600">This device has no repairs attached.</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold"
                onClick={() => setRemoveOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                disabled={
                  !canEditDevice ||
                  !onRemoveDevice ||
                  (repairs.length > 0 && (otherDevices.length === 0 || !reassignTo))
                }
                onClick={() => {
                  if (!onRemoveDevice) return;
                  void Promise.resolve(
                    onRemoveDevice(
                      device.id,
                      repairs.length > 0 ? reassignTo || undefined : undefined,
                    ),
                  ).then(() => {
                    setRemoveOpen(false);
                    toastSuccess('Device removed');
                  });
                }}
              >
                Remove device
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
